import { useContext } from 'react';
import { UnifiedSearchResultModel, Client as SearchClient, FilterModel } from '@bizzkit/ecommerce-search/client';
import { keepPreviousData, QueryClient, useQuery, UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '@/api/apiConfig';
import { productBandServerSideFetch } from '@/api/product/bands';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { ProblemDetails, SearchSettingsResponse } from '@/generated/data.schemas';
import { mapProductDetail } from '../product/mapping';
import { Filter, ProductDetail } from '../product/types';
import { mapInstalledEquipmentsResult, mapSearchFilters } from './mapping';
import { InstalledEquipment } from './types';

export interface FetchFilterUnitParams {
	readonly searchApiHost: string;
	readonly searchToken: string;
	readonly searchSegment: string;
	readonly searchableIdsFilterId: string;
	readonly detailsScopeId: string;
	readonly filterUnitId: string;
	filters?: Record<string, FilterModel>;
}

export interface FetchInstalledEquipmentsParams {
	readonly searchApiHost: string;
	readonly searchToken: string;
	readonly searchSegment: string;
	readonly installedEquipmentScopeId: string;
	readonly phrase: string;
}

const fetchInstalledEquipments = async (params: FetchInstalledEquipmentsParams): Promise<UnifiedSearchResultModel> => {
	const searchClient = new SearchClient({ baseUrl: params.searchApiHost });

	const result = await searchClient.search.search({
		segmentId: params.searchSegment,
		scopeId: params.installedEquipmentScopeId,
		authenticationToken: params.searchToken,
		phrase: params.phrase,
	});
	return result.data;
};

export const useInstalledEquipmentQuery = (
	id: string,
	settings?: SearchSettingsResponse,
	onDemand: boolean = false,
): UseQueryResult<InstalledEquipment[] | null | undefined, undefined | ProblemDetails> => {
	const params: FetchInstalledEquipmentsParams = {
		searchApiHost: settings?.searchApiHost ?? '',
		searchToken: settings?.searchToken ?? '',
		searchSegment: settings?.searchSegment ?? '',
		installedEquipmentScopeId: settings?.installedEquipmentScopeId ?? '',
		phrase: id,
	};

	return useQuery({
		queryKey: queryKeys.applicationDetail.installedEquipments(params).queryKey,
		queryFn: () => fetchInstalledEquipments(params),
		enabled: !!settings && id.length > 0 && onDemand,
		select: (model) => {
			if (model.products && model.products.length > 0) {
				return mapInstalledEquipmentsResult(model.products);
			}
			return [];
		},
		retry: false,
	});
};

const fetchFilterUnit = async (params: FetchFilterUnitParams): Promise<UnifiedSearchResultModel> => {
	const searchClient = new SearchClient({ baseUrl: params.searchApiHost });
	const filters = params.filters ? { ...params.filters } : {};
	filters[params.searchableIdsFilterId] = { values: [params.filterUnitId] };
	const result = await searchClient.search.search({
		segmentId: params.searchSegment,
		scopeId: params.detailsScopeId,
		authenticationToken: params.searchToken,
		filters: filters,
	});
	return result.data;
};

export const filterUnitServerSideFetch = async (
	queryClient: QueryClient,
	id: string,
	settings: SearchSettingsResponse,
	origin: string,
): Promise<void> => {
	const params: FetchFilterUnitParams = {
		searchApiHost: settings.searchApiHost,
		searchToken: settings.searchToken,
		searchSegment: settings.searchSegment,
		detailsScopeId: settings.detailsScopeId,
		searchableIdsFilterId: settings.searchableIdsFilterId,
		filterUnitId: id,
	};

	const data = await fetchFilterUnit(params);
	queryClient.setQueryData(queryKeys.appSolutionBuilder.filterUnit(params).queryKey, data);
	if (data.products && data.products.length > 0) {
		const detail = mapProductDetail(data.products[0], origin, settings);

		const currentVariant = detail.variants.find((x) => x.id === detail.id);
		if (currentVariant?.spareParts && currentVariant?.spareParts?.length > 0) {
			await productBandServerSideFetch(
				queryClient,
				currentVariant?.spareParts.map((x) => x.productId),
				settings,
			);
		}

		const insertProductIds = detail.inserts.map((i) => i.productId);
		if (insertProductIds.length > 0) {
			await productBandServerSideFetch(queryClient, insertProductIds, settings);
		}
	}
};

export const useFilterUnitQuery = (
	id: string,
	enabled: boolean,
	filters?: Filter[],
	settings?: SearchSettingsResponse,
): UseQueryResult<ProductDetail | null | undefined, undefined | ProblemDetails> => {
	const { url: pageOrigin } = useContext(SegmentationContext);

	const params: FetchFilterUnitParams = {
		searchApiHost: settings?.searchApiHost ?? '',
		searchToken: settings?.searchToken ?? '',
		searchSegment: settings?.searchSegment ?? '',
		detailsScopeId: settings?.detailsScopeId ?? '',
		searchableIdsFilterId: settings?.searchableIdsFilterId ?? '',
		filterUnitId: id,
		filters: mapSearchFilters(filters),
	};

	const runQuery = !!settings && id.length > 0 && enabled;
	return useQuery({
		queryKey: queryKeys.appSolutionBuilder.filterUnit(params).queryKey,
		queryFn: () => fetchFilterUnit(params),
		enabled: runQuery,
		placeholderData: keepPreviousData,
		select: (d) => {
			if (d.products && d.products.length > 0) {
				return mapProductDetail(d.products[0], pageOrigin, settings);
			}
			return null;
		},
		retry: false,
	});
};
